import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { quotes, submissions } from '../services/api.js';
import './BrokerQuoteView.css';

const STATUS_STEPS = ['submitted', 'in_review', 'quoted', 'bound'];

function getStepIndex(status) {
  if (!status) return 0;
  const normalized = status.toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'new' || normalized === 'submitted') return 0;
  if (normalized === 'in_review' || normalized === 'assigned') return 1;
  if (normalized === 'quoted' || normalized === 'delivered') return 2;
  if (normalized === 'bound' || normalized === 'accepted') return 3;
  if (normalized === 'declined' || normalized === 'expired') return -1;
  return 0;
}

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatKey(key) {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function BrokerQuoteView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [quote, setQuote] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // Try loading both the quote and the submission in parallel.
        // The id may be a quoteId or a submissionId depending on how the user arrived.
        const [quoteRes, subRes] = await Promise.allSettled([
          quotes.get(id),
          submissions.get(id),
        ]);

        if (quoteRes.status === 'fulfilled') {
          const qData = quoteRes.value.data || quoteRes.value;
          setQuote(qData);
        }
        if (subRes.status === 'fulfilled') {
          const sData = subRes.value.data || subRes.value;
          setSubmission(sData);
        }

        if (quoteRes.status === 'rejected' && subRes.status === 'rejected') {
          setError('Failed to load quote details');
        }
      } catch (err) {
        setError(err.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="bqv-loading">
        <div className="spinner" />
        <p>Loading quote...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bqv-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  const q = quote || {};
  const sub = submission || {};
  const status = sub.Status || q.Status || 'submitted';
  const step = getStepIndex(status);
  const isDeclined = status === 'declined' || status === 'expired';
  const progressPct = isDeclined
    ? 100
    : ((step + 1) / STATUS_STEPS.length) * 100;
  const progressColor = isDeclined
    ? 'var(--color-risk-red)'
    : status === 'bound'
      ? 'var(--color-risk-green)'
      : 'var(--color-primary)';

  // Display names from the enriched submission response
  const insuredName = sub.InsuredName || q.InsuredName || sub.AccountID || q.AccountID || 'Quote Details';
  const productName = sub.ProductName || q.ProductName || sub.ProductID || q.ProductID || '—';

  const terms = q.Terms || {};
  const comparison = q.CoverageComparison || null;
  const rationale = q.Rationale || null;

  return (
    <div className="bqv">
      {/* Back */}
      <div className="bqv__breadcrumb">
        <button className="bqv__back" onClick={() => navigate('/broker')}>
          &larr; Back to Submissions
        </button>
      </div>

      {/* Progress bar */}
      <div className="bqv__progress-section card">
        <div className="bqv__progress-bar">
          <div
            className="bqv__progress-fill"
            style={{ width: `${progressPct}%`, backgroundColor: progressColor }}
          />
        </div>
        <div className="bqv__progress-steps">
          {STATUS_STEPS.map((s, i) => (
            <div
              key={s}
              className={`bqv__step ${!isDeclined && i <= step ? 'bqv__step--done' : ''} ${isDeclined ? 'bqv__step--declined' : ''}`}
            >
              <div className="bqv__step-dot" />
              <span>{s.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </div>
        {isDeclined && (
          <p className="bqv__declined-msg">
            This submission has been {status === 'expired' ? 'expired' : 'declined'}.
          </p>
        )}
      </div>

      {/* Submission / Quote info header */}
      <div className="bqv__header card">
        <h1 className="bqv__insured">{insuredName}</h1>
        <div className="bqv__header-meta">
          <span>
            Product: <strong>{productName}</strong>
          </span>
          <span>
            Created: <strong>{formatDate(sub.CreatedAt || q.CreatedAt)}</strong>
          </span>
          {q.DeliveredAt && (
            <span>
              Delivered: <strong>{formatDate(q.DeliveredAt)}</strong>
            </span>
          )}
          {q.ExpiresAt && (
            <span>
              Expires: <strong>{formatDate(q.ExpiresAt)}</strong>
            </span>
          )}
          {q.Status && (
            <span>
              Quote Status:{' '}
              <strong style={{ textTransform: 'capitalize' }}>{q.Status}</strong>
            </span>
          )}
        </div>
      </div>

      {/* Quote detail cards: Premium, Deductible, Limit */}
      {(q.Premium != null || q.Deductible != null || q.Limit != null) && (
        <div className="bqv__quote-details">
          <div className="bqv__quote-card card">
            <span className="bqv__quote-label">Premium</span>
            <span className="bqv__quote-value">{formatCurrency(q.Premium)}</span>
          </div>
          <div className="bqv__quote-card card">
            <span className="bqv__quote-label">Deductible</span>
            <span className="bqv__quote-value">{formatCurrency(q.Deductible)}</span>
          </div>
          <div className="bqv__quote-card card">
            <span className="bqv__quote-label">Limit</span>
            <span className="bqv__quote-value">{formatCurrency(q.Limit)}</span>
          </div>
        </div>
      )}

      {/* Key terms */}
      {terms && typeof terms === 'object' && Object.keys(terms).length > 0 && (
        <div className="bqv__terms card">
          <h2 className="bqv__section-title">Key Terms</h2>
          <dl className="bqv__terms-grid">
            {Object.entries(terms).map(([key, value]) => (
              <div key={key} className="bqv__term-item">
                <dt>{formatKey(key)}</dt>
                <dd>
                  {typeof value === 'object'
                    ? JSON.stringify(value)
                    : String(value ?? '—')}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* Rationale */}
      {rationale && (
        <div className="bqv__rationale card">
          <h2 className="bqv__section-title">Decision Rationale</h2>
          <p className="bqv__rationale-text">{rationale}</p>
        </div>
      )}

      {/* Coverage comparison */}
      {comparison && Array.isArray(comparison) && comparison.length > 0 && (
        <div className="bqv__comparison card">
          <h2 className="bqv__section-title">Coverage Comparison</h2>
          <table className="bqv__comparison-table">
            <thead>
              <tr>
                <th>Coverage</th>
                <th>Proposed</th>
                <th>Current</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((row, idx) => (
                <tr key={idx}>
                  <td className="bqv__comp-coverage">
                    {row.Coverage || row.Name || '—'}
                  </td>
                  <td>{row.Proposed ?? row.ProposedValue ?? '—'}</td>
                  <td>{row.Current ?? row.CurrentValue ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* If no quote data yet */}
      {q.Premium == null &&
        q.Deductible == null &&
        q.Limit == null &&
        !rationale &&
        (!terms || Object.keys(terms).length === 0) && (
          <div className="bqv__pending card">
            <p>Quote details will appear here once underwriting is complete.</p>
          </div>
        )}
    </div>
  );
}
